/**
 * CQ 游戏启动弹窗
 * 半屏底部弹窗，展示游戏信息 + 启动按钮
 */
import { Dialog } from '@base-ui-components/react/dialog';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';

export interface CqLaunchGame {
  name: string;
  iconUrl?: string;
  gamecode?: string;
  gamehall?: string;
  /** 是否为大厅入口 */
  isLobby?: boolean;
}

interface CqLaunchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  game: CqLaunchGame | null;
}

export default function CqLaunchSheet({
  open,
  onOpenChange,
  game,
}: CqLaunchSheetProps) {
  if (!game) return null;

  const getLaunchUrl = () => {
    if (game.isLobby) {
      return '/cq-lobby';
    }
    return `/cq-launcher?gamecode=${game.gamecode}&gamehall=${game.gamehall}`;
  };

  const handleLaunch = () => {
    window.open(getLaunchUrl(), '_blank');
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* 遮罩层 */}
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 min-h-dvh bg-black/50',
            'z-[100]',
            'transition-opacity duration-300',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />

        {/* 底部弹窗 */}
        <Dialog.Popup
          className={cn(
            'fixed bottom-0 left-1/2 -translate-x-1/2',
            'w-full max-w-[460px]',
            'bg-bg-card rounded-t-2xl',
            'shadow-[0_-4px_16px_rgba(0,0,0,0.1)]',
            'transition-transform duration-300 ease-out',
            'data-[starting-style]:translate-y-full',
            'data-[ending-style]:translate-y-full',
            'flex flex-col',
            'outline-none',
            'pb-safe',
            'z-[101]'
          )}
        >
          {/* 拖动指示条 */}
          <div className="flex justify-center pt-3 pb-2 shrink-0">
            <div className="w-10 h-1 bg-border-color rounded-full" />
          </div>

          {/* 内容区域 */}
          <div className="px-5 pb-5 flex flex-col gap-3">
            {/* 游戏信息：左图标 + 右信息 */}
            <div className="flex items-center gap-4">
              {/* 游戏图标 */}
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
                {game.iconUrl ? (
                  <img
                    src={game.iconUrl}
                    alt={game.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">CQ</span>
                  </div>
                )}
              </div>
              {/* 游戏名称和描述 */}
              <div className="flex-1 min-w-0">
                <Dialog.Title className="text-lg font-semibold text-text-primary truncate">
                  {game.name}
                </Dialog.Title>
                <p className="text-sm text-text-secondary mt-0.5">
                  CQ9 {game.isLobby ? '游戏大厅' : '正版游戏'}
                </p>
              </div>
            </div>

            {/* 启动按钮 */}
            <Button
              variant="primary"
              block
              onClick={handleLaunch}
              className="h-12 text-base font-semibold rounded-xl"
            >
              启动游戏
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
